import Phaser from 'phaser';
import { Client, getStateCallbacks, type Room } from 'colyseus.js';
import { DERBY, MSG } from '@bb/shared';
import type { ProgressNews } from '../net/api';

export interface RaceHud {
  phase: string;
  countdown: number;
  lap: number;
  place: number;
  racers: number;
  elapsedMs: number;
}

export interface RaceEvents {
  /** Shared input state; the React touch buttons write into it too. */
  input: { throttle: number; steer: number };
  onHud: (h: RaceHud) => void;
  onResult: (r: { place: number | null; coins: number; xp: number; progress?: ProgressNews }) => void;
  onError: (message: string) => void;
}

interface KartState {
  name: string;
  color: string;
  x: number;
  y: number;
  angle: number;
  lap: number;
  place: number;
}

/** Driftwood Derby view. Everything shown comes from the server; we only send throttle and steering. */
export class RaceScene extends Phaser.Scene {
  private room?: Room;
  private opts!: RaceEvents;
  private karts = new Map<string, { root: Phaser.GameObjects.Container; s: KartState }>();
  private destroyed = false;
  private lastSent = '';
  private keys?: Record<string, Phaser.Input.Keyboard.Key>;

  constructor() {
    super('race');
  }
  init(data: RaceEvents) {
    this.opts = data;
  }

  create() {
    this.drawTrack();
    this.keys = this.input.keyboard?.addKeys('UP,DOWN,LEFT,RIGHT,W,A,S,D') as Record<string, Phaser.Input.Keyboard.Key>;
    this.sys.events.once(Phaser.Scenes.Events.DESTROY, () => {
      this.destroyed = true;
      void this.room?.leave(true);
    });
    void this.connect();
  }

  private async connect() {
    await new Promise((r) => setTimeout(r, 60));
    if (this.destroyed) return;
    try {
      const room = await new Client(`${window.location.origin}/realtime`).joinOrCreate('derby');
      if (this.destroyed) return void room.leave(true);
      this.room = room;
      const karts = getStateCallbacks(room)(room.state).karts as unknown as {
        onAdd: (cb: (k: KartState, key: string) => void) => void;
        onRemove: (cb: (k: KartState, key: string) => void) => void;
      };
      karts.onAdd((k, key) => this.addKart(key, k));
      karts.onRemove((_k, key) => {
        this.karts.get(key)?.root.destroy();
        this.karts.delete(key);
      });
      room.onMessage('result', (r) => this.opts.onResult(r));
      room.onMessage(MSG.progress, () => {});
    } catch (e) {
      this.opts.onError((e as Error).message || 'Could not join a race.');
    }
  }

  private addKart(key: string, s: KartState) {
    const g = this.add.graphics();
    const color = Phaser.Display.Color.HexStringToColor(s.color).color;
    g.fillStyle(0x1d2b36).fillRoundedRect(-14, -12, 8, 6, 2).fillRoundedRect(-14, 6, 8, 6, 2).fillRoundedRect(8, -12, 8, 6, 2).fillRoundedRect(8, 6, 8, 6, 2);
    g.fillStyle(color).fillRoundedRect(-18, -9, 36, 18, 6);
    g.fillStyle(0xffffff, 0.8).fillRoundedRect(2, -6, 10, 12, 3);
    const label = this.add
      .text(0, -26, s.name, { fontFamily: '"Baloo 2", system-ui', fontSize: '13px', fontStyle: '700', color: key === this.room?.sessionId ? '#14506e' : '#1d2b36', backgroundColor: key === this.room?.sessionId ? '#fdf1d2' : '#ffffffcc', padding: { x: 4, y: 0 } })
      .setOrigin(0.5);
    const body = this.add.container(0, 0, [g]);
    const root = this.add.container(s.x, s.y, [body, label]).setDepth(10);
    this.karts.set(key, { root, s });
  }

  override update(_t: number, dt: number) {
    // Input: keyboard plus the on-screen buttons.
    const k = this.keys;
    const up = k && (k.UP!.isDown || k.W!.isDown);
    const down = k && (k.DOWN!.isDown || k.S!.isDown);
    const left = k && (k.LEFT!.isDown || k.A!.isDown);
    const right = k && (k.RIGHT!.isDown || k.D!.isDown);
    const throttle = up ? 1 : down ? -1 : this.opts.input.throttle;
    const steer = left ? -1 : right ? 1 : this.opts.input.steer;
    const sig = `${throttle},${steer}`;
    if (this.room && sig !== this.lastSent) {
      this.room.send('input', { throttle, steer });
      this.lastSent = sig;
    }
    const f = Math.min(1, dt / 60);
    for (const { root, s } of this.karts.values()) {
      root.x += (s.x - root.x) * f;
      root.y += (s.y - root.y) * f;
      const body = root.list[0] as Phaser.GameObjects.Container;
      body.rotation = s.angle;
    }
    const st = this.room?.state as { phase: string; countdown: number; elapsedMs: number } | undefined;
    const mine = this.room ? this.karts.get(this.room.sessionId)?.s : undefined;
    if (st) this.opts.onHud({ phase: st.phase, countdown: st.countdown, elapsedMs: st.elapsedMs, lap: mine?.lap ?? 0, place: mine?.place ?? 0, racers: this.karts.size });
  }

  private drawTrack() {
    const g = this.add.graphics().setDepth(0);
    g.fillStyle(0x8fcf7a).fillRect(0, 0, DERBY.width, DERBY.height);
    const stadium = (r: number, color: number) => {
      g.fillStyle(color);
      g.fillRect(DERBY.cx - DERBY.halfLength, DERBY.cy - r, DERBY.halfLength * 2, r * 2);
      g.fillCircle(DERBY.cx - DERBY.halfLength, DERBY.cy, r);
      g.fillCircle(DERBY.cx + DERBY.halfLength, DERBY.cy, r);
    };
    stadium(DERBY.outer + 8, 0xd4553e);
    stadium(DERBY.outer, 0xe2cc95);
    stadium(DERBY.inner, 0xd4553e);
    stadium(DERBY.inner - 8, 0x79bb66);
    // Start/finish line at the top middle.
    for (let i = 0; i < 14; i++) {
      g.fillStyle(i % 2 ? 0xffffff : 0x1d2b36).fillRect(DERBY.cx - 6, DERBY.cy - DERBY.outer + i * 10, 12, 10);
    }
    this.add.text(DERBY.cx, DERBY.cy, 'Driftwood Derby', { fontFamily: '"Baloo 2", system-ui', fontSize: '42px', fontStyle: '800', color: '#ffffff' }).setOrigin(0.5).setAlpha(0.85);
  }
}
